/**
 * The account screen's glyphs, transcribed from legacy's `Account/index.jsx`
 * `Icon` component.
 *
 * Inline SVG rather than an icon dependency: these are a handful of fixed
 * paths, and a font or a package would be a network request and a build step
 * to render them. The reminder wizard draws four of them (bell, lock and both
 * chevrons) at its own size, which is what the `size` prop is for.
 *
 * Every one is `aria-hidden`. They are decoration beside a label that already
 * says the thing — the back control carries its name on the link's own
 * `aria-label`, and the settings rows carry theirs in visible text — so
 * announcing the glyph as well would read the same item twice.
 */

/** The account rows' glyph size in CSS pixels, as legacy draws them. */
const ACCOUNT_ICON_SIZE = 18;

/**
 * Shared geometry for the stroked glyphs — legacy's `common` props, verbatim,
 * at the given edge length.
 *
 * @param size - edge length in CSS pixels.
 * @returns the props to spread onto the `<svg>`.
 */
export function strokeProps(size: number) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  } as const;
}

const STROKE_PROPS = strokeProps(ACCOUNT_ICON_SIZE);

/** Props for the glyphs another screen draws at a different size. */
export interface SizedIconProps {
  /** Edge length in CSS pixels; the account rows' 18 when omitted. */
  size?: number;
}

/** The paid badge's crown. Filled, and smaller than the stroked set, like legacy's. */
export function CrownIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
      <path d="M5 16L3 6l5.5 4L12 4l3.5 6L21 6l-2 10z" />
    </svg>
  );
}

/** The top bar's back chevron. */
export function ChevronLeftIcon({ size = ACCOUNT_ICON_SIZE }: SizedIconProps): React.JSX.Element {
  return (
    <svg {...strokeProps(size)}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

/** The trailing chevron on each settings row. */
export function ChevronRightIcon({ size = ACCOUNT_ICON_SIZE }: SizedIconProps): React.JSX.Element {
  return (
    <svg {...strokeProps(size)}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

/**
 * Reflection reminders.
 *
 * The row it labels opens a wizard rather than navigating, so unlike the three
 * links below it, this glyph sits on a `<button>`.
 */
export function BellIcon({ size = ACCOUNT_ICON_SIZE }: SizedIconProps): React.JSX.Element {
  return (
    <svg {...strokeProps(size)}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

/** Privacy Policy. */
export function LockIcon({ size = ACCOUNT_ICON_SIZE }: SizedIconProps): React.JSX.Element {
  return (
    <svg {...strokeProps(size)}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

/** Terms of Use. */
export function DocumentIcon(): React.JSX.Element {
  return (
    <svg {...STROKE_PROPS}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M9 13h6M9 17h6" />
    </svg>
  );
}

/** Help & feedback. */
export function HelpIcon(): React.JSX.Element {
  return (
    <svg {...STROKE_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/**
 * Dismiss, in this set's lighter rounded stroke — the dialogs' cross is
 * `overlay/CloseIcon`.
 *
 * An icon rather than a `×` text node: a literal that reads as punctuation to
 * a linter is still copy to a screen reader, and the button's `aria-label` is
 * what names it.
 */
export function CloseIcon(): React.JSX.Element {
  return (
    <svg {...STROKE_PROPS}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
